import React, { useEffect, useMemo, useRef, useState } from "react";

/**
 * CreditLimitRobo
 * A robot mascot whose face/color react to credit-limit usage, plus an
 * inline "Change limit" request flow.
 *
 * Mood thresholds:  <50% happy (green) · 50-79.9% medium (amber) · ≥80% high (red)
 *
 * Change-limit flow:
 *   1. "Change limit" -> modal opens, prefilled with current limit.
 *   2. Submit:
 *      - below outstanding balance -> blocked
 *      - increase -> applied instantly, no confirmation
 *      - same/decrease (still ≥ balance) -> asks to confirm
 *   3. onLimitChange(newLimit) fires the moment it's applied. Pass `loading`
 *      back in while an async save is in flight; the modal shows
 *      "Updating…" and waits for it to turn false before closing.
 *
 * Requires Tailwind. See KEYFRAMES for the two custom animations.
 */

export type CreditMood = "happy" | "medium" | "high";
type PanelState = "closed" | "editing" | "confirming" | "applied";

export interface CreditLimitRoboProps {
  /** Current outstanding balance. Negative = advance payment, treated as 0 usage. */
  outstandingBalance: number;
  creditLimit: number;
  currency?: string;
  locale?: string;
  /** Fired as soon as a new limit is applied (auto increase or confirmed decrease). */
  onLimitChange?: (newLimit: number) => void;
  onPrimary?: () => void;
  onSecondary?: () => void;
  /** True while the parent is saving; keeps the "applied" step open until it clears. */
  loading?: boolean;
}

const MOOD_COPY: Record<CreditMood, { line: string; action: string }> = {
  happy: { line: "You're well within limit.", action: "Looks good" },
  medium: { line: "Getting close — worth keeping an eye on.", action: "Keep watching" },
  high: { line: "Almost at the limit.", action: "Review account" },
};

/** Static Tailwind classes per mood, written literally so the JIT scanner picks them up. */
const MOOD_STYLES: Record<
  CreditMood,
  { text: string; bg: string; border: string; ghostBorder: string }
> = {
  happy: { text: "text-emerald-600", bg: "bg-emerald-600", border: "border-emerald-200", ghostBorder: "border-emerald-300" },
  medium: { text: "text-amber-600", bg: "bg-amber-600", border: "border-amber-200", ghostBorder: "border-amber-300" },
  high: { text: "text-red-600", bg: "bg-red-600", border: "border-red-200", ghostBorder: "border-red-300" },
};

const moodFromPercent = (pct: number): CreditMood =>
  pct >= 80 ? "high" : pct >= 50 ? "medium" : "happy";

const useReducedMotion = () =>
  typeof window !== "undefined" &&
  !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

/* ------------------------------------------------------------------ */
/*  Root                                                               */
/* ------------------------------------------------------------------ */

export default function CreditLimitRobo({
  outstandingBalance,
  creditLimit,
  currency = "QAR",
  locale = "en-IN",
  onLimitChange,
  onPrimary,
  onSecondary,
  loading,
}: CreditLimitRoboProps) {
  const reduced = useReducedMotion();
  const used = Math.max(outstandingBalance, 0);
  const percentUsed = creditLimit > 0 ? Math.min((used / creditLimit) * 100, 999) : 0;
  const mood = moodFromPercent(percentUsed);
  const m = MOOD_STYLES[mood];
  const availableCredit = outstandingBalance < 0 ? creditLimit : Math.max(creditLimit - outstandingBalance, 0);

  const money = useMemo(
    () => new Intl.NumberFormat(locale, { style: "currency", currency, maximumFractionDigits: 0 }),
    [locale, currency]
  );

  const [panelOpen, setPanelOpen] = useState(false);
  const openPanel = () => {
    setPanelOpen(true);
    onSecondary?.();
  };
  const closePanel = () => setPanelOpen(false);

  return (
    <div className="w-full rounded-lg bg-slate-100 p-2 font-sans text-slate-900">
      <style>{KEYFRAMES}</style>

      <div className="flex flex-wrap items-start gap-3.5">
        <Robot mood={mood} waving={!reduced} />
        <GreetingBubble mood={mood} onChangeLimit={openPanel} />
      </div>

      <div className="flex gap-2 mt-3">
        <StatCard label="Credit Limit" value={money.format(creditLimit)} />
        <StatCard label="Available Credit" value={money.format(availableCredit)} valueClass={m.text} />
      </div>

      {panelOpen && (
        <ChangeLimitModal
          creditLimit={creditLimit}
          used={used}
          money={money}
          mood={mood}
          loading={loading}
          onClose={closePanel}
          onApply={(newLimit) => onLimitChange?.(newLimit)}
        />
      )}

      <UsageGauge percentUsed={percentUsed} used={used} creditLimit={creditLimit} money={money} mood={mood} />

      {onPrimary && (
        <div className="flex gap-2.5 mt-4">
          <button className={`rounded-md px-3.5 py-2 font-semibold text-white ${m.bg}`} onClick={onPrimary}>
            {MOOD_COPY[mood].action}
          </button>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Greeting bubble (typing effect + change-limit button)             */
/* ------------------------------------------------------------------ */

function GreetingBubble({ mood, onChangeLimit }: { mood: CreditMood; onChangeLimit: () => void }) {
  const m = MOOD_STYLES[mood];
  const greeting = "Hi I am Majilis assistant";
  const reduced = useReducedMotion();
  const [typed, setTyped] = useState(reduced ? greeting : "");
  const [revealed, setRevealed] = useState(reduced);

  useEffect(() => {
    if (reduced) return;
    setTyped("");
    setRevealed(false);
    let i = 0;
    const id = window.setInterval(() => {
      i += 1;
      setTyped(greeting.slice(0, i));
      if (i >= greeting.length) {
        window.clearInterval(id);
        window.setTimeout(() => setRevealed(true), 200);
      }
    }, 45);
    return () => window.clearInterval(id);
  }, [reduced]);

  return (
    <div className="min-w-0 flex-1 grid gap-2 rounded-tl-[4px] rounded-tr-2xl rounded-b-2xl border border-black/10 bg-white px-3.5 py-2.5 mt-1.5">
      <div>
        <span className="font-medium">
          {typed}
          {!revealed && (
            <span className={`ml-0.5 inline-block h-[1em] w-[2px] align-[-2px] animate-pulse ${m.bg}`} aria-hidden="true" />
          )}
        </span>
        <br />
        {revealed && <span className={`text-sm font-semibold ${m.text}`}>{MOOD_COPY[mood].line}</span>}
      </div>

      <button
        type="button"
        onClick={onChangeLimit}
        className={`mt-1.5 flex-none rounded-md border bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 ${m.ghostBorder}`}
      >
        Change limit
      </button>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Stat card (Credit Limit / Available Credit)                       */
/* ------------------------------------------------------------------ */

function StatCard({ label, value, valueClass = "" }: { label: string; value: string; valueClass?: string }) {
  return (
    <div className="flex-1 rounded-md border border-black/10 bg-white px-2.5 py-2 grid gap-0.5">
      <span className="text-[11px] text-slate-500">{label}</span>
      <span className={`text-sm font-bold tabular-nums ${valueClass}`}>{value}</span>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Usage gauge                                                        */
/* ------------------------------------------------------------------ */

function UsageGauge({
  percentUsed,
  used,
  creditLimit,
  money,
  mood,
}: {
  percentUsed: number;
  used: number;
  creditLimit: number;
  money: Intl.NumberFormat;
  mood: CreditMood;
}) {
  const m = MOOD_STYLES[mood];
  return (
    <div className="grid gap-1.5 mt-4">
      <div className="relative h-2.5 overflow-hidden rounded-full bg-black/10">
        <div
          className={`h-full rounded-full transition-all duration-500 ease-out ${m.bg}`}
          style={{ width: `${Math.min(percentUsed, 100)}%` }}
        />
        <div className="absolute inset-y-0 left-1/2 w-px bg-white/70" />
        <div className="absolute inset-y-0 left-[80%] w-px bg-white/70" />
      </div>
      <div className="flex justify-between text-xs text-slate-500 tabular-nums">
        <span>{percentUsed.toFixed(0)}% of limit used</span>
        <span>{money.format(used)} / {money.format(creditLimit)}</span>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Change-limit modal                                                 */
/* ------------------------------------------------------------------ */

function ChangeLimitModal({
  creditLimit,
  used,
  money,
  mood,
  loading,
  onClose,
  onApply,
}: {
  creditLimit: number;
  used: number;
  money: Intl.NumberFormat;
  mood: CreditMood;
  loading?: boolean;
  onClose: () => void;
  onApply: (newLimit: number) => void;
}) {
  const m = MOOD_STYLES[mood];
  const [step, setStep] = useState<Exclude<PanelState, "closed">>("editing");
  const [inputValue, setInputValue] = useState(String(creditLimit));
  const [pendingLimit, setPendingLimit] = useState<number | null>(null);
  const [appliedLimit, setAppliedLimit] = useState<number | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    window.setTimeout(() => inputRef.current?.focus(), 0);
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !loading) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [loading, onClose]);

  // Auto-dismiss once applied and the parent's async save (if any) has settled.
  useEffect(() => {
    if (step !== "applied" || loading) return;
    const t = window.setTimeout(onClose, 1800);
    return () => window.clearTimeout(t);
  }, [step, loading, onClose]);

  const handleSubmit = () => {
    const requested = Number(inputValue);
    if (!inputValue.trim() || Number.isNaN(requested) || requested <= 0) {
      setFormError("Enter a valid amount.");
      return;
    }
    if (requested < used) {
      setFormError(`Can't be below the outstanding balance of ${money.format(used)}.`);
      return;
    }
    setFormError(null);

    if (requested > creditLimit) {
      setAppliedLimit(requested);
      setStep("applied");
      onApply(requested);
      return;
    }
    setPendingLimit(requested);
    setStep("confirming");
  };

  const handleConfirm = () => {
    if (pendingLimit == null) return;
    setAppliedLimit(pendingLimit);
    setStep("applied");
    onApply(pendingLimit);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="clr-modal-title"
      onClick={(e) => {
        if (e.target === e.currentTarget && !loading) onClose();
      }}
    >
      <div className="w-full max-w-sm rounded-lg bg-white p-4 shadow-xl grid gap-3.5">
        <div className="flex items-start justify-between gap-3">
          <h2 id="clr-modal-title" className="text-base font-semibold text-slate-900">
            Change credit limit
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={!!loading}
            aria-label="Close"
            className="flex-none rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 disabled:opacity-40"
          >
            ✕
          </button>
        </div>

        {step === "editing" && (
          <EditStep
            inputRef={inputRef}
            inputValue={inputValue}
            setInputValue={(v) => { setInputValue(v); setFormError(null); }}
            formError={formError}
            used={used}
            money={money}
            mood={mood}
            onSubmit={handleSubmit}
            onCancel={onClose}
          />
        )}

        {step === "confirming" && pendingLimit != null && (
          <ConfirmStep
            creditLimit={creditLimit}
            pendingLimit={pendingLimit}
            used={used}
            money={money}
            onConfirm={handleConfirm}
            onBack={() => setStep("editing")}
          />
        )}

        {step === "applied" && appliedLimit != null && (
          <AppliedStep loading={loading} increased={appliedLimit > creditLimit} newLimit={money.format(appliedLimit)} />
        )}
      </div>
    </div>
  );
}

function EditStep({
  inputRef,
  inputValue,
  setInputValue,
  formError,
  used,
  money,
  mood,
  onSubmit,
  onCancel,
}: {
  inputRef: React.RefObject<HTMLInputElement>;
  inputValue: string;
  setInputValue: (v: string) => void;
  formError: string | null;
  used: number;
  money: Intl.NumberFormat;
  mood: CreditMood;
  onSubmit: () => void;
  onCancel: () => void;
}) {
  const m = MOOD_STYLES[mood];
  return (
    <div className="grid gap-2.5">
      <label htmlFor="clr-new-limit" className="text-sm font-medium text-slate-700">
        New credit limit
      </label>
      <input
        id="clr-new-limit"
        ref={inputRef}
        type="number"
        min={0}
        inputMode="decimal"
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && onSubmit()}
        className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm tabular-nums focus:border-slate-500 focus:outline-none"
      />
      <p className="text-xs text-slate-500">
        Outstanding balance is {money.format(used)}. The new limit can't go below that.
      </p>
      {formError && <p className="text-xs font-medium text-red-600">{formError}</p>}

      <div className="flex gap-2 mt-1">
        <button type="button" onClick={onSubmit} className={`flex-1 rounded-md px-4 py-2 text-sm font-semibold text-white ${m.bg}`}>
          Submit
        </button>
        <button type="button" onClick={onCancel} className="flex-none rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50">
          Cancel
        </button>
      </div>
    </div>
  );
}

function ConfirmStep({
  creditLimit,
  pendingLimit,
  used,
  money,
  onConfirm,
  onBack,
}: {
  creditLimit: number;
  pendingLimit: number;
  used: number;
  money: Intl.NumberFormat;
  onConfirm: () => void;
  onBack: () => void;
}) {
  return (
    <div className="grid gap-2.5">
      <p className="text-sm text-slate-700">
        Confirm reducing the limit from{" "}
        <span className="font-semibold tabular-nums">{money.format(creditLimit)}</span> to{" "}
        <span className="font-semibold tabular-nums">{money.format(pendingLimit)}</span>? Available credit will
        become <span className="font-semibold tabular-nums">{money.format(Math.max(pendingLimit - used, 0))}</span>.
      </p>
      <div className="flex gap-2">
        <button type="button" onClick={onConfirm} className="flex-1 rounded-md bg-amber-600 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-700">
          Confirm
        </button>
        <button type="button" onClick={onBack} className="flex-none rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50">
          Back
        </button>
      </div>
    </div>
  );
}

function AppliedStep({ loading, increased, newLimit }: { loading?: boolean; increased: boolean; newLimit: string }) {
  if (loading) {
    return (
      <div className="rounded-md border border-slate-300 bg-slate-50 p-3.5 flex items-center gap-2.5">
        <span className="h-4 w-4 flex-none rounded-full border-2 border-slate-300 border-t-slate-600 [animation:clr-spin_0.7s_linear_infinite]" aria-hidden="true" />
        <p className="text-sm font-medium text-slate-700">Updating credit limit…</p>
      </div>
    );
  }
  return (
    <div className="rounded-md border border-emerald-300 bg-emerald-50 p-3.5">
      <p className="text-sm font-medium text-emerald-800">
        {increased ? `Increased automatically to ${newLimit}.` : `Limit updated to ${newLimit}.`}
      </p>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  The robot                                                          */
/* ------------------------------------------------------------------ */

function Robot({ mood, waving }: { mood: CreditMood; waving: boolean }) {
  const m = MOOD_STYLES[mood];
  return (
    <svg
      className={`flex-none ${m.text}`}
      viewBox="0 0 150 168"
      width="120"
      height="134"
      role="img"
      aria-label={`Robot assistant, ${mood} about current credit usage`}
    >
      <ellipse cx="75" cy="158" rx="40" ry="6" fill="rgba(0,0,0,.22)" />

      <line x1="75" y1="18" x2="75" y2="30" stroke="#8a97ad" strokeWidth="3" />
      <circle cx="75" cy="14" r="5" fill="currentColor" />

      <rect x="18" y="92" width="14" height="34" rx="7" fill="#2b3a55" />
      <g className={waving ? "origin-[118px_88px] [animation:clr-wave_0.55s_ease-in-out_4]" : ""}>
        <rect x="118" y="88" width="14" height="36" rx="7" fill="#2b3a55" />
        <circle cx="125" cy="126" r="9" fill="#3a4c6d" />
      </g>

      <rect x="30" y="86" width="90" height="62" rx="12" fill="#2b3a55" />
      <rect x="30" y="86" width="90" height="62" rx="12" fill="url(#clr-sheen)" />
      <rect x="41" y="98" width="17" height="13" rx="3" fill="#d9c27a" />
      <path d="M41 104.5h17M49.5 98v13" stroke="rgba(20,28,52,.45)" strokeWidth="1.2" />
      <rect x="41" y="128" width="46" height="4" rx="2" fill="rgba(238,242,251,.35)" />
      <rect x="41" y="136" width="28" height="4" rx="2" fill="rgba(238,242,251,.2)" />

      <rect x="34" y="30" width="82" height="58" rx="20" fill="#3a4c6d" />
      <rect x="44" y="42" width="62" height="34" rx="17" fill="#16213a" />

      {mood === "high" && (
        <g>
          <path d="M53 49 L67 54" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
          <path d="M97 49 L83 54" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
        </g>
      )}

      <g>
        <rect className="[animation:clr-blink_4.4s_infinite]" x="57" y="54" width="9" height="13" rx="4.5" fill="currentColor" />
        <rect className="[animation:clr-blink_4.4s_infinite]" x="84" y="54" width="9" height="13" rx="4.5" fill="currentColor" />
      </g>

      {mood === "happy" && <path d="M58 73 Q75 82 92 73" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" />}
      {mood === "medium" && <path d="M60 76 H90" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" />}
      {mood === "high" && <path d="M58 80 Q75 71 92 80" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" />}

      <defs>
        <linearGradient id="clr-sheen" x1="30" y1="86" x2="120" y2="148">
          <stop offset="0" stopColor="#fff" stopOpacity=".14" />
          <stop offset="1" stopColor="#fff" stopOpacity="0" />
        </linearGradient>
      </defs>
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/*  Custom keyframes (Tailwind has no built-ins for these)            */
/* ------------------------------------------------------------------ */

const KEYFRAMES = `
@keyframes clr-wave { 0%,100% { transform: rotate(0deg); } 50% { transform: rotate(-22deg); } }
@keyframes clr-blink { 0%,94%,100% { transform: scaleY(1); } 97% { transform: scaleY(.15); } }
@keyframes clr-spin { to { transform: rotate(360deg); } }
@media (prefers-reduced-motion: reduce) {
  * { animation: none !important; }
}
`;